import {
  RightMainContainer,
  HeaderContainer,
  PrimaryHeading,
  IconWrapper,
  MainButton,
  InputField,
  BodyContainer,
  TextEditor,
  SecondaryHeading,
  PreviewCard,
  AddComponent,
  PopOver,
  DropDownCard,
  LoadingListCards,
  Loader,
  DeleteButton,
} from "commonUI";
import { BackArrow, SaveIcon } from "allIcons";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  useDeleteNoteTemplate,
  useTemplateNotes,
  useUpdateNoteTemplate,
} from "api/templateLibrary/notes";
import { useNotification } from "notification";
import { useSelector } from "react-redux";
import { getUserSavePermission, isEqual, parse } from "helpers";
import { useTags } from "api/siteSettings/Tags";
import { useAddNoteTemplate } from "api/templateLibrary/notes";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function AddEditNotes({
  previousPath = `/${ModulePrefixes.TEMPLATE_LIBRARY}/notes`,
  isSiteTemplate,
}) {
  const { data: tags, isLoading: tagsLoading, error: tagsError } = useTags();
  const { data: notes, isLoading: noteLoading } = useTemplateNotes();
  const [inputValues, setInputValues] = useState({
    title: "",
    message: "",
    tags: [],
  });
  const [openTagModal, setOpenTagModal] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const notification = useNotification();
  const { userData } = useSelector((state) => state.user);
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();

  useEffect(() => {
    if (isEqual(id, "add")) return;
    if (noteLoading) return;
    const note = notes.find((note) => isEqual(note.id, id));
    if (note) {
      setInputValues({
        title: note.title,
        message: note.message,
        tags: parse(note.tags) || [],
      });
    }
  }, [notes, id, noteLoading]);

  const { mutate: addNote, isLoading: addingNote } = useAddNoteTemplate(
    `/${ModulePrefixes.TEMPLATE_LIBRARY}/notes/`
  );

  const updateMutation = useUpdateNoteTemplate();

  const { mutate: deleteMutation, isLoading: deleteMutationIsLoading } =
    useDeleteNoteTemplate(previousPath);

  const handleSaveChanges = () => {
    if (inputValues.title === "") {
      notification({
        message: t("common.input.error.titleRequired"),
        type: "error",
      });
      return;
    }
    if (isEqual(id, "add")) {
      addNote({
        ...inputValues,
        requested_from: [
          {
            user_avatar: userData.user_avatar,
            role: userData.rolesData?.[0]?.role_title ?? "Client",
            group: "Admin",
            name: userData.name,
            id: userData.id,
          },
        ],
      });
    }
    if (id !== "add") {
      updateMutation.mutate({
        ...inputValues,
        id,
      });
    }
  };

  return (
    <>
      <RightMainContainer>
        {addingNote || updateMutation.isLoading || deleteMutationIsLoading ? (
          <Loader loader="block" />
        ) : (
          ""
        )}
        <HeaderContainer>
          <IconWrapper>
            <BackArrow onClick={() => navigate(previousPath)} />
            <PrimaryHeading className="clamp-text">
              {isEqual(id.toLowerCase(), "add")
                ? t("libraryTemplate.notes.addNote")
                : inputValues.title}
            </PrimaryHeading>
          </IconWrapper>
          <div className="flex items-center justify-center gap-14">
            <DeleteButton
              show={!isSiteTemplate && id !== "add" && delete_templates}
              onDelete={() => deleteMutation(id)}
              deleteTitle={t("libraryTemplate.delete.title")}
              deleteButtonText={t("libraryTemplate.delete.button")}
              deleteMessage={t("libraryTemplate.delete.message")}
            />
            {getUserSavePermission({
              id,
              addPermission: add_new_templates,
              editPermission: edit_templates,
            }) ? (
              <MainButton onClick={handleSaveChanges}>
                <SaveIcon fill="white" />
                {t("button.saveChanges")}
              </MainButton>
            ) : (
              ""
            )}
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            placeholder={t("common.input.title.placeholder")}
            label={t("common.input.title.label")}
            value={inputValues.title}
            setValue={(value) =>
              setInputValues({
                ...inputValues,
                title: value,
              })
            }
          />
          <TextEditor
            placeholder={t("common.input.description.placeholder")}
            label={t("common.input.description.label")}
            height={150}
            value={inputValues.message}
            setValue={(value) =>
              setInputValues({
                ...inputValues,
                message: value,
              })
            }
            reRender={
              isEqual(id, "add") ? false : inputValues.message ? true : false
            }
          />
          <div className="mt-2">
            <SecondaryHeading>{t("common.input.tags.label")}</SecondaryHeading>
            {inputValues.tags?.length > 0 ? (
              <div className="flex items-center gap-5 flex-wrap">
                {inputValues.tags?.map((tag, index) => (
                  <PreviewCard
                    color={tag.color}
                    title={tag.text}
                    icon={true}
                    className="m-0"
                    onCross={() =>
                      setInputValues((prev) => ({
                        ...prev,
                        tags: prev.tags.filter((_, i) => i !== index),
                      }))
                    }
                  />
                ))}
              </div>
            ) : (
              ""
            )}

            <AddComponent
              title={t("common.input.tags.addTag")}
              value={openTagModal}
              onClick={() => setOpenTagModal(true)}
            >
              <PopOver
                open={openTagModal}
                onClose={() => setOpenTagModal(false)}
                style={{
                  bottom: "calc(100% + 15px)",
                  top: "unset",
                  right: "unset",
                  left: "unset",
                  width: "270px",
                }}
                toolTip={{
                  horizontal: "center",
                  vertical: "bottom",
                }}
              >
                {tagsError ? (
                  <p className="body-text p-3">
                    {t("common.input.tags.cannotLoaded")}
                  </p>
                ) : tagsLoading ? (
                  <div className="p-3">
                    <LoadingListCards rows={3} listClass={"h-8"} />
                  </div>
                ) : (
                  <DropDownCard
                    title={t("common.input.tags.label")}
                    listStyle
                    filterSelectedItems
                    className="relative-important"
                    selectedValues={inputValues.tags.map((item) => item.id)}
                    list={tags?.map((ele) => ({
                      ...ele,
                      index: ele.id,
                      text: ele.tag_title,
                    }))}
                    onSelect={(item) => {
                      if (
                        inputValues.tags.find((i) => isEqual(i.id, item.id))
                      ) {
                        return;
                      }
                      setInputValues((pre) => ({
                        ...pre,
                        tags: [
                          ...pre.tags,
                          {
                            id: item.id,
                            text: item.tag_title,
                            color: item.color,
                          },
                        ],
                      }));
                    }}
                  />
                )}
              </PopOver>
            </AddComponent>
          </div>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
